import React, { useEffect, useState, useRef, useLayoutEffect } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";
import {
  MapPin,
  Phone,
  Clock,
  ArrowLeft,
  ArrowRight,
  CheckCircle,
  Loader2,
  ChevronLeft,
  ChevronRight,
  Info,
  ChevronDown,
  ChevronUp,
  CreditCard,
  DollarSign,
  Mail,
  Navigation,
  Users,
  User as UserIcon,
} from "lucide-react";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MAX_SERVICES = 3;

const DEFAULT_BRANDING = {
  primaryColor: "#7C3AED",
  secondaryColor: "#4F46E5",
  accentColor: "#7C3AED",
};

/**
 * Renders a service description clamped to 2 lines. If the text overflows,
 * an inline ⓘ info icon is rendered at the bottom-right (over a tiny
 * white fade mask so it sits cleanly next to the "..." ellipsis). Clicking
 * it calls `onInfoClick` (typically opens a modal with the full text).
 */
const DescriptionWithInfo = ({ text, primaryColor, onInfoClick }) => {
  const ref = useRef(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const check = () => {
      // 1px tolerance to avoid sub-pixel flicker
      setIsTruncated(el.scrollHeight - 1 > el.clientHeight);
    };
    check();
    let ro;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(check);
      ro.observe(el);
    }
    window.addEventListener("resize", check);
    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener("resize", check);
    };
  }, [text]);

  return (
    <div
      className="relative"
      style={{ minHeight: "2.5rem" }}
      data-testid="service-description-block"
    >
      <p
        ref={ref}
        className="text-sm text-gray-600 leading-snug line-clamp-2 break-words whitespace-pre-line"
      >
        {text || "\u00A0"}
      </p>
      {isTruncated && (
        <button
          type="button"
          aria-label="Show full description"
          onClick={(e) => {
            e.stopPropagation();
            onInfoClick && onInfoClick();
          }}
          className="absolute right-0 bottom-0 pl-6 flex items-center hover:opacity-80 transition-opacity"
          style={{
            background:
              "linear-gradient(to right, rgba(255,255,255,0) 0%, #ffffff 60%)",
            color: primaryColor,
          }}
          data-testid="service-description-info-btn"
        >
          <Info className="w-4 h-4" />
        </button>
      )}
    </div>
  );
};

const PublicBookingPage = () => {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [businessData, setBusinessData] = useState(null);
  const [paymentSettings, setPaymentSettings] = useState(null);
  const [step, setStep] = useState(0);
  const [availability, setAvailability] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [bookingComplete, setBookingComplete] = useState(false);
  const [bookingResult, setBookingResult] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [showSummary, setShowSummary] = useState(false);
  const [paymentProcessing, setPaymentProcessing] = useState(false);

  // Multi-service booking state
  const [selectedServices, setSelectedServices] = useState([]);
  const [serviceBookings, setServiceBookings] = useState([]);
  const [expandedCards, setExpandedCards] = useState({});
  const [descriptionModal, setDescriptionModal] = useState({
    open: false,
    name: "",
    description: "",
  });

  const [customerInfo, setCustomerInfo] = useState({
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    notes: "",
  });

  // Top-nav tabs on the landing (step 0) screen. Default to "services".
  const [activeTab, setActiveTab] = useState("services");
  // About → Hours row collapsible (collapsed by default)
  const [hoursExpanded, setHoursExpanded] = useState(false);
  // Staff bio modal (used from Staff-selection step and Team tab)
  const [bioModal, setBioModal] = useState({ open: false, member: null });
  // Public booking page: which category accordions are currently expanded
  // (only used when business.show_categories_on_public is true)
  const [expandedCategories, setExpandedCategories] = useState({});
  // Modal shown when the user tries to select more than MAX_SERVICES.
  // Contact-the-salon nudge so they can confirm extended availability.
  const [maxServicesModalOpen, setMaxServicesModalOpen] = useState(false);

  const getBranding = () => {
    if (businessData?.business?.branding) {
      return { ...DEFAULT_BRANDING, ...businessData.business.branding };
    }
    return DEFAULT_BRANDING;
  };

  const branding = getBranding();

  // Handle payment success/cancel from URL params
  useEffect(() => {
    const paymentSuccess = searchParams.get("payment_success");
    const sessionId = searchParams.get("session_id");
    const paymentId = searchParams.get("payment_id");
    const paymentCancelled = searchParams.get("payment_cancelled");

    if (paymentSuccess === "true" && sessionId && paymentId) {
      setPaymentProcessing(true);
      handlePaymentSuccess(sessionId, paymentId);
    } else if (paymentCancelled === "true") {
      toast.error("Payment was cancelled. Please try again.");
      // Clean URL
      window.history.replaceState({}, "", `/book/${slug}`);
    }
  }, [searchParams, slug]);

  const handlePaymentSuccess = async (sessionId, paymentId) => {
    try {
      // Poll for payment status
      let attempts = 0;
      const maxAttempts = 10;

      const pollStatus = async () => {
        const statusResponse = await axios.get(
          `${API_URL}/stripe/checkout-status/${sessionId}`,
        );

        if (statusResponse.data.payment_status === "paid") {
          // Complete the booking
          const bookingResponse = await axios.post(
            `${API_URL}/stripe/complete-booking/${paymentId}`,
          );
          setBookingResult(bookingResponse.data);
          setBookingComplete(true);
          toast.success("Payment successful! Booking confirmed.");
          // Clean URL
          window.history.replaceState({}, "", `/book/${slug}`);
        } else if (statusResponse.data.status === "expired") {
          toast.error("Payment session expired. Please try again.");
          window.history.replaceState({}, "", `/book/${slug}`);
        } else if (attempts < maxAttempts) {
          attempts++;
          setTimeout(pollStatus, 2000);
        } else {
          toast.error("Could not verify payment. Please contact the business.");
          window.history.replaceState({}, "", `/book/${slug}`);
        }
      };

      await pollStatus();
    } catch (error) {
      console.error("Payment verification failed:", error);
      toast.error("Failed to verify payment. Please contact the business.");
      window.history.replaceState({}, "", `/book/${slug}`);
    } finally {
      setPaymentProcessing(false);
    }
  };

  useEffect(() => {
    fetchBusinessData();
  }, [slug]);

  const fetchBusinessData = async () => {
    try {
      const [businessResponse, paymentResponse] = await Promise.all([
        axios.get(`${API_URL}/businesses/public/${slug}`),
        axios
          .get(`${API_URL}/businesses/public/${slug}/payment-settings`)
          .catch(() => ({ data: { deposit_required: false } })),
      ]);
      setBusinessData(businessResponse.data);
      setPaymentSettings(paymentResponse.data);
    } catch (error) {
      console.error("Failed to fetch business:", error);
      toast.error("Business not found");
    } finally {
      setLoading(false);
    }
  };

  const toLocalYMD = (date) => {
    if (!date) return null;
    const pad = (n) => String(n).padStart(2, "0");
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
  };

  const isTimeSlotPast = (slotTime, selectedDate) => {
    if (!selectedDate || !slotTime) return false;
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const selected = new Date(
      selectedDate.getFullYear(),
      selectedDate.getMonth(),
      selectedDate.getDate(),
    );
    if (selected.getTime() !== today.getTime()) return false;
    const [hours, minutes] = slotTime.split(":").map(Number);
    const slotDate = new Date();
    slotDate.setHours(hours, minutes, 0, 0);
    return slotDate <= now;
  };

  const getStepInfo = () => {
    if (step === 0) return { phase: "services", serviceIdx: -1 };
    const N = selectedServices.length;
    const perServiceSteps = 2 * N;
    if (step <= perServiceSteps) {
      const inLoop = step - 1; // 0-indexed offset within per-service loop
      const serviceIdx = Math.floor(inLoop / 2);
      const phase = inLoop % 2 === 0 ? "staff" : "datetime";
      return { phase, serviceIdx };
    }
    if (step === perServiceSteps + 1)
      return { phase: "details", serviceIdx: -1 };
    return { phase: "confirm", serviceIdx: -1 };
  };

  const stepInfo = getStepInfo();
  const currentServiceIdx = stepInfo.serviceIdx;

  const fetchAvailability = async (
    serviceId,
    date,
    excludeSlots = [],
    staffId = null,
  ) => {
    try {
      const dateStr = toLocalYMD(date);
      const params = { service_id: serviceId, date: dateStr };
      if (staffId) params.staff_id = staffId;
      if (excludeSlots.length > 0) {
        params.exclude_slots = excludeSlots
          .map((s) => `${s.time}|${s.staff_id || ""}|${s.duration || 30}`)
          .join(",");
      }
      const response = await axios.get(
        `${API_URL}/businesses/${businessData.business.id}/availability`,
        { params },
      );
      setAvailability(response.data.slots || []);
    } catch (error) {
      console.error("Failed to fetch availability:", error);
      setAvailability([]);
    }
  };

  const handleServiceToggle = (serviceId) => {
    setSelectedServices((prev) => {
      if (prev.includes(serviceId)) {
        return prev.filter((id) => id !== serviceId);
      }
      if (prev.length >= MAX_SERVICES) {
        setMaxServicesModalOpen(true);
        return prev;
      }
      return [...prev, serviceId];
    });
  };

  const getCurrentService = () => {
    if (currentServiceIdx < 0 || currentServiceIdx >= selectedServices.length)
      return null;
    return businessData?.services?.find(
      (s) => s.id === selectedServices[currentServiceIdx],
    );
  };

  const getCurrentBooking = () => {
    if (currentServiceIdx < 0) return null;
    return (
      serviceBookings[currentServiceIdx] || {
        service_id: selectedServices[currentServiceIdx],
        staff_id: null,
        staff_chosen: false,
        date: null,
        time: "",
      }
    );
  };

  const updateCurrentBooking = (updates) => {
    const idx = currentServiceIdx;
    if (idx < 0) return;
    setServiceBookings((prev) => {
      const newBookings = [...prev];
      while (newBookings.length <= idx) {
        newBookings.push({
          service_id: selectedServices[newBookings.length],
          staff_id: null,
          staff_chosen: false,
          date: null,
          time: "",
        });
      }
      newBookings[idx] = { ...newBookings[idx], ...updates };
      return newBookings;
    });
  };

  const getExcludeSlots = () => {
    return serviceBookings
      .slice(0, currentServiceIdx)
      .filter((b) => b && b.date && b.time)
      .map((b) => {
        const service = businessData?.services?.find(
          (s) => s.id === b.service_id,
        );
        return {
          time: b.time,
          staff_id: b.staff_id,
          date: b.date,
          duration: service?.duration || 30,
        };
      });
  };

  const getSelectedServicesInfo = () => {
    return selectedServices
      .map((id) => businessData?.services?.find((s) => s.id === id))
      .filter(Boolean);
  };

  const getTotalPrice = () => {
    return getSelectedServicesInfo().reduce(
      (sum, s) => sum + (s.price || 0),
      0,
    );
  };

  const getTotalDuration = () => {
    return getSelectedServicesInfo().reduce(
      (sum, s) => sum + (s.duration || 0),
      0,
    );
  };

  const getDepositAmount = () => {
    if (!paymentSettings?.deposit_required) return 0;
    return (
      Math.round(
        getTotalPrice() * (paymentSettings.deposit_percentage / 100) * 100,
      ) / 100
    );
  };

  const getRemainingAmount = () => {
    return Math.round((getTotalPrice() - getDepositAmount()) * 100) / 100;
  };

  const getTotalSteps = () => {
    // For display purposes, treat 0 selected as if 1 will be selected so the
    // user sees "Step 1 of 5" (Services → Staff → Time → Details → Confirm)
    // even before they pick a service. Once they pick more, it grows.
    const effective = Math.max(1, selectedServices.length);
    return 1 + 2 * effective + 2;
  };

  const canProceed = () => {
    if (step === 0) return selectedServices.length > 0;
    if (stepInfo.phase === "staff") {
      const booking = getCurrentBooking();
      return booking && booking.staff_chosen === true;
    }
    if (stepInfo.phase === "datetime") {
      const booking = getCurrentBooking();
      return booking && booking.date && booking.time;
    }
    if (stepInfo.phase === "details") {
      return (
        customerInfo.customer_name.trim() && customerInfo.customer_email.trim()
      );
    }
    return true;
  };

  const handleNext = () => {
    if (!canProceed()) {
      if (step === 0) toast.error("Please select at least one service");
      else if (stepInfo.phase === "staff")
        toast.error("Please choose a staff member or 'Anyone'");
      else if (stepInfo.phase === "datetime")
        toast.error("Please select date and time");
      else if (stepInfo.phase === "details")
        toast.error("Please fill in your name and email");
      return;
    }
    setStep((s) => s + 1);
  };

  const handleBack = () => {
    setStep((s) => Math.max(0, s - 1));
  };

  useEffect(() => {
    if (stepInfo.phase === "datetime" && currentServiceIdx >= 0) {
      const booking = serviceBookings[currentServiceIdx];
      const staffId = booking?.staff_id || null;
      if (!booking?.date) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        updateCurrentBooking({ date: today });
        fetchAvailability(
          selectedServices[currentServiceIdx],
          today,
          getExcludeSlots(),
          staffId,
        );
      } else {
        fetchAvailability(
          selectedServices[currentServiceIdx],
          booking.date,
          getExcludeSlots(),
          staffId,
        );
      }
    }
  }, [step]);

  const handleSubmit = async () => {
    if (
      !customerInfo.customer_name.trim() ||
      !customerInfo.customer_email.trim()
    ) {
      toast.error("Please fill in your name and email");
      return;
    }

    setSubmitting(true);
    try {
      // Check if deposit is required
      if (
        paymentSettings?.deposit_required &&
        paymentSettings?.deposit_percentage > 0
      ) {
        // Create checkout session and redirect to Stripe
        const appointmentData = {
          appointments: serviceBookings.map((b) => ({
            service_id: b.service_id,
            staff_id: b.staff_id || null,
            date: toLocalYMD(b.date),
            time: b.time,
          })),
          customer_name: customerInfo.customer_name,
          customer_email: customerInfo.customer_email,
          customer_phone: customerInfo.customer_phone,
          notes: customerInfo.notes,
        };

        const response = await axios.post(
          `${API_URL}/stripe/create-checkout-session`,
          {
            business_id: businessData.business.id,
            appointment_data: appointmentData,
            origin_url: window.location.origin,
          },
        );

        // Redirect to Stripe Checkout
        window.location.href = response.data.checkout_url;
        return;
      }

      // No deposit required - book directly
      if (serviceBookings.length === 1) {
        const booking = serviceBookings[0];
        const response = await axios.post(
          `${API_URL}/businesses/${businessData.business.id}/appointments`,
          {
            service_id: booking.service_id,
            staff_id: booking.staff_id || null,
            date: toLocalYMD(booking.date),
            time: booking.time,
            customer_name: customerInfo.customer_name,
            customer_email: customerInfo.customer_email,
            customer_phone: customerInfo.customer_phone,
            notes: customerInfo.notes,
          },
        );
        setBookingResult(response.data);
      } else {
        const response = await axios.post(
          `${API_URL}/businesses/${businessData.business.id}/appointments/batch`,
          {
            appointments: serviceBookings.map((b) => ({
              service_id: b.service_id,
              staff_id: b.staff_id || null,
              date: toLocalYMD(b.date),
              time: b.time,
            })),
            customer_name: customerInfo.customer_name,
            customer_email: customerInfo.customer_email,
            customer_phone: customerInfo.customer_phone,
            notes: customerInfo.notes,
          },
        );
        setBookingResult(response.data);
      }
      setBookingComplete(true);
      toast.success("Booking confirmed!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create booking");
    } finally {
      setSubmitting(false);
    }
  };

  // Calendar Component
  const CustomCalendar = ({ selected, onSelect, businessHours = [] }) => {
    const [viewDate, setViewDate] = useState(selected || new Date());

    const daysInMonth = new Date(
      viewDate.getFullYear(),
      viewDate.getMonth() + 1,
      0,
    ).getDate();
    const firstDayOfMonth = new Date(
      viewDate.getFullYear(),
      viewDate.getMonth(),
      1,
    ).getDay();
    const monthNames = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];
    const dayNames = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const closedJsDays = new Set(
      businessHours.filter((h) => h.is_closed).map((h) => (h.day + 1) % 7),
    );

    const isPast = (day) => {
      const date = new Date(viewDate.getFullYear(), viewDate.getMonth(), day);
      return date < today;
    };

    const isClosed = (day) => {
      const date = new Date(viewDate.getFullYear(), viewDate.getMonth(), day);
      return closedJsDays.has(date.getDay());
    };

    const isSelected = (day) => {
      if (!selected) return false;
      return (
        selected.getDate() === day &&
        selected.getMonth() === viewDate.getMonth() &&
        selected.getFullYear() === viewDate.getFullYear()
      );
    };

    const isToday = (day) => {
      const date = new Date(viewDate.getFullYear(), viewDate.getMonth(), day);
      return date.toDateString() === today.toDateString();
    };

    const days = [];
    for (let i = 0; i < firstDayOfMonth; i++) {
      days.push(<div key={`empty-${i}`} className="h-10 md:h-12"></div>);
    }
    for (let day = 1; day <= daysInMonth; day++) {
      const past = isPast(day);
      const closed = isClosed(day);
      const unavailable = past || closed;
      const sel = isSelected(day);
      const todayDate = isToday(day);

      days.push(
        <button
          key={day}
          onClick={() =>
            !unavailable &&
            onSelect(new Date(viewDate.getFullYear(), viewDate.getMonth(), day))
          }
          disabled={unavailable}
          title={closed ? "Store closed" : undefined}
          className={`relative h-10 w-10 md:h-12 md:w-12 mx-auto rounded-full flex items-center justify-center text-base md:text-lg font-medium transition-all duration-200
            ${unavailable ? "cursor-not-allowed" : "cursor-pointer hover:bg-gray-100"}
            ${past ? "text-gray-300" : ""}
            ${closed && !past ? "text-gray-400" : ""}
            ${sel ? "text-white" : ""}
            ${todayDate && !sel ? "font-bold" : ""}`}
          style={
            sel
              ? { backgroundColor: branding.primaryColor, color: "white" }
              : todayDate && !closed
                ? { color: branding.primaryColor }
                : {}
          }
        >
          {day}
          {closed && (
            <span
              aria-hidden="true"
              className="absolute inset-0 flex items-center justify-center pointer-events-none"
            >
              <svg
                viewBox="0 0 40 40"
                className="w-full h-full absolute inset-0"
                xmlns="http://www.w3.org/2000/svg"
              >
                <line
                  x1="10"
                  y1="20"
                  x2="30"
                  y2="20"
                  stroke="#d1d5db"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </svg>
            </span>
          )}
        </button>,
      );
    }

    return (
      <div className="w-full">
        <div className="flex items-center justify-between mb-4 px-2">
          <button
            onClick={() =>
              setViewDate(
                new Date(viewDate.getFullYear(), viewDate.getMonth() - 1, 1),
              )
            }
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <ChevronLeft className="w-5 h-5 text-gray-600" />
          </button>
          <h3 className="text-lg md:text-xl font-bold text-gray-900">
            {monthNames[viewDate.getMonth()]} {viewDate.getFullYear()}
          </h3>
          <button
            onClick={() =>
              setViewDate(
                new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1),
              )
            }
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <ChevronRight className="w-5 h-5 text-gray-600" />
          </button>
        </div>
        <div className="grid grid-cols-7 mb-2">
          {dayNames.map((day) => (
            <div
              key={day}
              className="h-10 flex items-center justify-center text-sm font-medium text-gray-500"
            >
              {day}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-y-1">{days}</div>
      </div>
    );
  };

  // Payment processing screen
  if (paymentProcessing) {
    return (
      <div className="h-screen flex flex-col items-center justify-center bg-white">
        <Loader2 className="w-12 h-12 animate-spin text-primary mb-4" />
        <h2 className="text-xl font-bold">Processing your payment...</h2>
        <p className="text-gray-500 mt-2">
          Please wait while we confirm your booking.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="animate-pulse text-gray-500">Loading...</div>
      </div>
    );
  }

  if (!businessData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-2">Business Not Found</h1>
          <p className="text-gray-500 mb-4">
            The booking page you're looking for doesn't exist.
          </p>
          <Link to="/">
            <Button>Go Home</Button>
          </Link>
        </div>
      </div>
    );
  }

  const { business, services } = businessData;
  const currentService = getCurrentService();
  const currentBooking = getCurrentBooking();

  const headerGradientStyle = {
    background: `linear-gradient(135deg, ${branding.primaryColor} 0%, ${branding.secondaryColor} 100%)`,
  };

  // Booking complete screen
  if (bookingComplete) {
    return (
      <div
        className="h-screen flex flex-col bg-white overflow-hidden"
        data-testid="booking-complete"
      >
        <header
          className="flex-shrink-0 px-4 py-4 md:px-6 md:py-5"
          style={headerGradientStyle}
        >
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 md:gap-4">
              {business.logo_url ? (
                <img
                  src={business.logo_url}
                  alt={business.name}
                  className="w-10 h-10 md:w-14 md:h-14 rounded-xl md:rounded-2xl object-cover bg-white/20"
                />
              ) : (
                <div className="w-10 h-10 md:w-14 md:h-14 rounded-xl md:rounded-2xl bg-white/20 flex items-center justify-center">
                  <span className="text-white font-bold text-lg md:text-xl">
                    {business.name?.[0]}
                  </span>
                </div>
              )}
              <div className="flex-1 min-w-0">
                <h1 className="text-white text-lg md:text-2xl font-bold truncate">
                  {business.name}
                </h1>
                {business.address && (
                  <div className="flex items-center gap-1.5 text-white/90 text-sm">
                    <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
                    <span className="truncate">{business.address}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-6">
          <div className="max-w-md mx-auto text-center">
            <div
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6 mt-2"
              style={{ backgroundColor: `${branding.primaryColor}15` }}
            >
              <CheckCircle
                className="w-4 h-4"
                style={{ color: branding.primaryColor }}
              />
              <span
                className="text-sm font-semibold"
                style={{ color: branding.primaryColor }}
              >
                Booking Confirmed
              </span>
            </div>
            <h2 className="text-2xl font-bold mb-2">Thank You!</h2>
            <p className="text-gray-600 mb-6">
              {(bookingResult?.appointments?.length || serviceBookings.length) >
              1
                ? `Your ${bookingResult?.appointments?.length || serviceBookings.length} appointments have been scheduled.`
                : "Your appointment has been scheduled."}
              <br />A confirmation email has been sent to{" "}
              <span className="font-medium">
                {bookingResult?.customer_email || customerInfo.customer_email}
              </span>
            </p>

            <div className="bg-gray-50 rounded-2xl p-4 mb-6 text-left space-y-3">
              {/* Use bookingResult.appointments if available (from Stripe payment flow), otherwise use serviceBookings */}
              {(
                bookingResult?.appointments ||
                serviceBookings.map((b, idx) => {
                  const service = businessData?.services?.find(
                    (s) => s.id === b.service_id,
                  );
                  return {
                    service_name: service?.name,
                    date: b.date?.toLocaleDateString("en-US", {
                      weekday: "short",
                      month: "short",
                      day: "numeric",
                    }),
                    time: b.time,
                    service_price: service?.price,
                  };
                })
              ).map((apt, idx, arr) => (
                <div
                  key={idx}
                  className={idx > 0 ? "pt-3 border-t border-gray-200" : ""}
                >
                  {arr.length > 1 && (
                    <div className="text-xs text-gray-500 mb-1">
                      Appointment {idx + 1}
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-gray-600">Service</span>
                    <span className="font-medium">{apt.service_name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Date</span>
                    <span className="font-medium">{apt.date}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Time</span>
                    <span className="font-medium">{apt.time}</span>
                  </div>
                </div>
              ))}

              {/* Payment summary */}
              {bookingResult?.deposit_amount > 0 && (
                <div className="pt-3 border-t border-gray-200">
                  <div className="flex justify-between text-green-600">
                    <span>Deposit Paid</span>
                    <span className="font-semibold">
                      ${bookingResult.deposit_amount.toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between text-gray-600">
                    <span>Due at appointment</span>
                    <span className="font-medium">
                      ${bookingResult.remaining_amount.toFixed(2)}
                    </span>
                  </div>
                </div>
              )}

              {/* Total for multiple services */}
              {((bookingResult?.appointments?.length ||
                serviceBookings.length) > 1 ||
                bookingResult?.total_amount > 0) && (
                <div className="pt-3 border-t border-gray-200">
                  <div className="flex justify-between">
                    <span className="font-semibold">Total</span>
                    <span
                      className="font-bold"
                      style={{ color: branding.primaryColor }}
                    >
                      $
                      {(bookingResult?.total_amount || getTotalPrice()).toFixed(
                        2,
                      )}
                    </span>
                  </div>
                </div>
              )}
            </div>

            <Button
              onClick={() => window.location.reload()}
              className="w-full h-12 rounded-xl"
              style={{ backgroundColor: branding.primaryColor }}
            >
              Book Another Appointment
            </Button>
          </div>
        </main>

        <footer className="flex-shrink-0 border-t border-gray-100 py-3 bg-gray-50">
          <div className="text-center text-xs text-gray-500">
            © {new Date().getFullYear()} {business.name}. Powered by SlotBook.
          </div>
        </footer>
      </div>
    );
  }

  // Build step labels
  const buildStepLabels = () => {
    const labels = ["Services"];
    selectedServices.forEach((id) => {
      const svc = businessData?.services?.find((s) => s.id === id);
      const name = svc?.name || "Service";
      labels.push(`${name} • Staff`);
      labels.push(`${name} • Time`);
    });
    labels.push("Details", "Confirm");
    return labels;
  };
  const stepLabels =
    selectedServices.length > 0
      ? buildStepLabels()
      : ["Services", "Staff", "Time", "Details", "Confirm"];

  return (
    <div
      className="h-screen flex flex-col bg-white overflow-hidden"
      data-testid="public-booking-page"
    >
      {/* Fixed Header */}
      <header
        className="flex-shrink-0 px-4 py-4 md:px-6 md:py-5"
        style={headerGradientStyle}
      >
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-3 md:gap-4">
            {business.logo_url ? (
              <img
                src={business.logo_url}
                alt={business.name}
                className="w-10 h-10 md:w-14 md:h-14 rounded-xl md:rounded-2xl object-cover bg-white/20"
              />
            ) : (
              <div className="w-10 h-10 md:w-14 md:h-14 rounded-xl md:rounded-2xl bg-white/20 flex items-center justify-center">
                <span className="text-white font-bold text-lg md:text-xl">
                  {business.name?.[0]}
                </span>
              </div>
            )}
            <div className="flex-1 min-w-0">
              <h1 className="text-white text-lg md:text-2xl font-bold truncate">
                {business.name}
              </h1>
              {business.address && (
                <div className="flex items-center gap-1.5 text-white/90 text-sm">
                  <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
                  <span className="truncate">{business.address}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Top Tab Navigation (only on landing/services step) */}
      {step === 0 && (
        <nav
          className="flex-shrink-0 bg-white border-b border-gray-200 px-4"
          data-testid="public-booking-tabs"
          aria-label="Business sections"
        >
          <div className="max-w-4xl mx-auto flex items-stretch gap-1 overflow-x-auto">
            {[
              { id: "about", label: "About" },
              { id: "services", label: "Services" },
              { id: "team", label: "Team" },
            ].map((tab) => {
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  data-testid={`tab-${tab.id}`}
                  aria-selected={isActive}
                  className="relative py-3 px-4 sm:px-6 text-sm font-semibold transition-colors whitespace-nowrap"
                  style={{
                    color: isActive ? branding.primaryColor : "#6B7280",
                  }}
                >
                  {tab.label}
                  {isActive && (
                    <span
                      className="absolute left-2 right-2 bottom-0 h-0.5 rounded-full"
                      style={{ backgroundColor: branding.primaryColor }}
                    />
                  )}
                </button>
              );
            })}
          </div>
        </nav>
      )}

      {/* Selected Services Summary Bar */}
      {step > 0 && selectedServices.length > 0 && (
        <div
          className="flex-shrink-0 bg-white border-b border-gray-200 px-4 py-2.5 cursor-pointer hover:bg-gray-50 transition-colors"
          onClick={() => setShowSummary(!showSummary)}
        >
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <div
                  className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: `${branding.primaryColor}15` }}
                >
                  <Clock
                    className="w-4 h-4"
                    style={{ color: branding.primaryColor }}
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 text-sm">
                    <span className="font-medium">
                      {selectedServices.length} service
                      {selectedServices.length > 1 ? "s" : ""}
                    </span>
                    <span className="text-gray-300">·</span>
                    <span className="text-gray-500">
                      {getTotalDuration()} min
                    </span>
                    <span className="text-gray-300">·</span>
                    <span
                      className="font-semibold"
                      style={{ color: branding.primaryColor }}
                    >
                      ${getTotalPrice().toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
              {showSummary ? (
                <ChevronUp className="w-5 h-5 text-gray-400" />
              ) : (
                <ChevronDown className="w-5 h-5 text-gray-400" />
              )}
            </div>

            {showSummary && (
              <div className="mt-3 pt-3 border-t border-gray-100 space-y-2">
                {getSelectedServicesInfo().map((svc, idx) => {
                  const booking = serviceBookings[idx];
                  return (
                    <div
                      key={svc.id}
                      className="flex items-center justify-between text-sm"
                    >
                      <span className="text-gray-700">{svc.name}</span>
                      <span className="text-gray-500">
                        {booking?.date && booking?.time
                          ? `${booking.date.toLocaleDateString("en-US", { month: "short", day: "numeric" })} ${booking.time}`
                          : "Not selected"}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Progress Bar - only show on Services flow */}
      {(step > 0 || activeTab === "services") && (
        <div className="flex-shrink-0 bg-white border-b px-4 py-2">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center justify-between text-xs mb-1">
              <span className="text-gray-500">
                Step {step + 1} of {getTotalSteps()}
              </span>
              <span
                className="font-medium"
                style={{ color: branding.primaryColor }}
              >
                {stepLabels[step]}
              </span>
            </div>
            <div className="h-1 bg-gray-100 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-300"
                style={{
                  width: `${((step + 1) / getTotalSteps()) * 100}%`,
                  backgroundColor: branding.primaryColor,
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Scrollable Main Content */}
      <main className="flex-1 overflow-y-auto">
        <div className="max-w-4xl mx-auto p-4 md:p-6">
          {/* Step 0: Select Services (only on Services tab) */}
          {step === 0 && activeTab === "services" && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold">Select Services</h2>
              </div>

              {selectedServices.length > 1 && (
                <div className="flex items-start gap-2 p-3 bg-blue-50 rounded-xl text-sm text-blue-700">
                  <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>
                    You'll select date & time for each service in sequence.
                  </span>
                </div>
              )}

              <div className="flex flex-col gap-3">
                {(() => {
                  const activeServices = services.filter(
                    (s) => s.is_active !== false,
                  );
                  const showFlag =
                    !!businessData?.business?.show_descriptions_on_public;
                  const groupByCategory =
                    !!businessData?.business?.show_categories_on_public;

                  // Render one service card (Layout A or Layout B based on showFlag)
                  const renderService = (service) => {
                    const isSelected = selectedServices.includes(service.id);
                    const hasImage = !!service.image_url;

                    if (!showFlag) {
                      // ===== LAYOUT A — descriptions OFF (original) =====
                      return (
                        <div
                          key={service.id}
                          onClick={() => handleServiceToggle(service.id)}
                          data-testid={`service-${service.id}`}
                          className={`relative flex flex-row items-center rounded-2xl border-2 transition-all cursor-pointer overflow-hidden ${
                            isSelected
                              ? "shadow-md"
                              : "border-gray-200 hover:border-gray-300 hover:shadow-sm"
                          }`}
                          style={
                            isSelected
                              ? {
                                  borderColor: branding.primaryColor,
                                  backgroundColor: `${branding.primaryColor}05`,
                                  minHeight: 120,
                                }
                              : { backgroundColor: "#ffffff", minHeight: 120 }
                          }
                        >
                          {hasImage && (
                            <img
                              src={service.image_url}
                              alt={service.name}
                              style={{
                                width: 130,
                                height: 114,
                                minWidth: 130,
                                minHeight: 114,
                                objectFit: "cover",
                                objectPosition: "center",
                                display: "block",
                                filter: "grayscale(100%)",
                                borderRadius: "12px",
                                margin: "8px",
                              }}
                            />
                          )}
                          <div className="flex-1 px-4 py-4 flex flex-col justify-center min-w-0">
                            <h3 className="font-bold text-base text-gray-900 mb-1 break-words">
                              {service.name}
                            </h3>
                            <div className="flex items-center gap-1 text-sm text-gray-400 mb-1.5">
                              <Clock className="w-3.5 h-3.5 flex-shrink-0" />
                              <span>{service.duration} min</span>
                            </div>
                            <p className="text-sm font-semibold text-gray-800">
                              ${service.price.toFixed(2)}
                            </p>
                          </div>
                          <div className="flex items-center pr-4 pl-2 flex-shrink-0">
                            {isSelected ? (
                              <div
                                className="w-6 h-6 rounded-full flex items-center justify-center"
                                style={{
                                  backgroundColor: branding.primaryColor,
                                }}
                              >
                                <CheckCircle className="w-4 h-4 text-white" />
                              </div>
                            ) : (
                              <ChevronRight className="w-5 h-5 text-gray-400" />
                            )}
                          </div>
                        </div>
                      );
                    }

                    // ===== LAYOUT B — descriptions ON =====
                    return (
                      <div
                        key={service.id}
                        onClick={() => handleServiceToggle(service.id)}
                        data-testid={`service-${service.id}`}
                        className={`relative flex flex-row items-stretch rounded-2xl border-2 transition-all cursor-pointer overflow-hidden ${
                          isSelected
                            ? "shadow-md"
                            : "border-gray-200 hover:border-gray-300 hover:shadow-sm"
                        }`}
                        style={
                          isSelected
                            ? {
                                borderColor: branding.primaryColor,
                                backgroundColor: `${branding.primaryColor}05`,
                                minHeight: 152,
                              }
                            : { backgroundColor: "#ffffff", minHeight: 152 }
                        }
                      >
                        {hasImage && (
                          <img
                            src={service.image_url}
                            alt={service.name}
                            style={{
                              width: 130,
                              minWidth: 130,
                              objectFit: "cover",
                              objectPosition: "center",
                              display: "block",
                              filter: "grayscale(100%)",
                              borderRadius: "12px",
                              margin: "8px",
                            }}
                            className="self-stretch"
                          />
                        )}
                        <div className="flex-1 px-4 py-3 flex flex-col min-w-0">
                          <div className="flex items-start justify-between gap-3 mb-1.5">
                            <h3 className="font-bold text-base text-gray-900 break-words line-clamp-1 flex-1 min-w-0">
                              {service.name}
                            </h3>
                            <div className="flex-shrink-0 mt-0.5">
                              {isSelected ? (
                                <div
                                  className="w-6 h-6 rounded-full flex items-center justify-center"
                                  style={{
                                    backgroundColor: branding.primaryColor,
                                  }}
                                >
                                  <CheckCircle className="w-4 h-4 text-white" />
                                </div>
                              ) : (
                                <ChevronRight className="w-5 h-5 text-gray-400" />
                              )}
                            </div>
                          </div>
                          <DescriptionWithInfo
                            text={service.description || ""}
                            primaryColor={branding.primaryColor}
                            onInfoClick={() =>
                              setDescriptionModal({
                                open: true,
                                name: service.name,
                                description: service.description || "",
                              })
                            }
                          />
                          <div className="mt-auto pt-2 border-t border-gray-100 flex items-center justify-between text-sm">
                            <span className="font-semibold text-gray-800">
                              ${service.price.toFixed(2)}
                            </span>
                            <span className="inline-flex items-center gap-1 text-gray-600">
                              <Clock className="w-3.5 h-3.5 flex-shrink-0" />
                              {service.duration} min
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  };

                  // FLAT layout — used when categorization is disabled or
                  // there are no categories configured at all.
                  if (!groupByCategory) {
                    return activeServices.map(renderService);
                  }

                  const cats = businessData?.categories || [];
                  // Group services by category_id
                  const byCat = {};
                  const uncategorized = [];
                  activeServices.forEach((s) => {
                    if (s.category_id && cats.some((c) => c.id === s.category_id)) {
                      (byCat[s.category_id] = byCat[s.category_id] || []).push(s);
                    } else {
                      uncategorized.push(s);
                    }
                  });

                  const renderAccordion = (key, name, items, color) => {
                    if (!items || items.length === 0) return null;
                    const isOpen = expandedCategories[key] !== false; // default open
                    const selectedCount = items.filter((s) =>
                      selectedServices.includes(s.id),
                    ).length;
                    return (
                      <div
                        key={key}
                        data-testid={`category-section-${key}`}
                        className="rounded-2xl border border-gray-200 bg-white overflow-hidden"
                      >
                        <button
                          type="button"
                          onClick={() =>
                            setExpandedCategories((prev) => ({
                              ...prev,
                              [key]: prev[key] === false ? true : false,
                            }))
                          }
                          aria-expanded={isOpen}
                          aria-controls={`category-panel-${key}`}
                          data-testid={`category-toggle-${key}`}
                          className="w-full flex items-center justify-between gap-3 px-4 py-4 hover:bg-gray-50 transition-colors min-h-[56px]"
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            {color && (
                              <span
                                className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                                style={{ backgroundColor: color }}
                              />
                            )}
                            <h3 className="font-bold text-base text-gray-900 truncate">
                              {name}
                            </h3>
                            <span className="text-xs font-medium text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full flex-shrink-0">
                              {items.length}
                            </span>
                            {selectedCount > 0 && (
                              <span
                                className="text-xs font-semibold px-2 py-0.5 rounded-full flex-shrink-0"
                                style={{
                                  backgroundColor: `${branding.primaryColor}15`,
                                  color: branding.primaryColor,
                                }}
                              >
                                {selectedCount} selected
                              </span>
                            )}
                          </div>
                          <ChevronDown
                            className={`w-5 h-5 text-gray-500 flex-shrink-0 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
                          />
                        </button>
                        <div
                          id={`category-panel-${key}`}
                          className={`grid transition-all duration-300 ease-out ${isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}
                        >
                          <div className="overflow-hidden">
                            <div className="px-3 pb-3 pt-1 flex flex-col gap-3">
                              {items.map(renderService)}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  };

                  return (
                    <>
                      {cats.map((c) =>
                        renderAccordion(c.id, c.name, byCat[c.id] || [], c.color),
                      )}
                      {uncategorized.length > 0 &&
                        renderAccordion(
                          "uncategorized",
                          "Other Services",
                          uncategorized,
                          null,
                        )}
                    </>
                  );
                })()}
              </div>

              {selectedServices.length > 0 && (
                <div className="p-4 bg-gray-50 rounded-2xl">
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-gray-600">Total Duration</span>
                    <span className="font-medium">
                      {getTotalDuration()} min
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Total</span>
                    <span
                      className="font-bold text-xl"
                      style={{ color: branding.primaryColor }}
                    >
                      ${getTotalPrice().toFixed(2)}
                    </span>
                  </div>
                  {/* Deposit info */}
                  {paymentSettings?.deposit_required &&
                    paymentSettings?.deposit_percentage > 0 && (
                      <div className="mt-3 pt-3 border-t border-gray-200">
                        <div className="flex items-center gap-2 text-sm text-gray-600 mb-1">
                          <CreditCard className="w-4 h-4" />
                          <span>
                            Deposit required (
                            {paymentSettings.deposit_percentage}%)
                          </span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-500">Pay now</span>
                          <span
                            className="font-semibold"
                            style={{ color: branding.primaryColor }}
                          >
                            ${getDepositAmount().toFixed(2)}
                          </span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-500">
                            Pay at appointment
                          </span>
                          <span className="font-medium">
                            ${getRemainingAmount().toFixed(2)}
                          </span>
                        </div>
                      </div>
                    )}
                </div>
              )}
            </div>
          )}

          {/* About Tab */}
          {step === 0 && activeTab === "about" && (
            <div className="space-y-5" data-testid="tab-content-about">
              {business.description && (
                <section>
                  <h2 className="text-xl font-bold mb-2">About us</h2>
                  <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                    {business.description}
                  </p>
                </section>
              )}

              {(() => {
                const dayLabels = [
                  "Monday",
                  "Tuesday",
                  "Wednesday",
                  "Thursday",
                  "Friday",
                  "Saturday",
                  "Sunday",
                ];
                const dayShort = [
                  "Mon",
                  "Tue",
                  "Wed",
                  "Thu",
                  "Fri",
                  "Sat",
                  "Sun",
                ];
                const fmt = (t) => {
                  if (!t) return "";
                  const [hh, mm] = t.split(":").map(Number);
                  if (isNaN(hh)) return t;
                  const ampm = hh >= 12 ? "pm" : "am";
                  const h12 = hh % 12 === 0 ? 12 : hh % 12;
                  const mmStr = String(mm || 0).padStart(2, "0");
                  return `${h12}:${mmStr} ${ampm}`;
                };
                const hours = business?.business_hours || [];
                const now = new Date();
                // Backend day convention: 0=Monday..6=Sunday
                const todayIdx = (now.getDay() + 6) % 7;
                const todayRow = hours.find((h) => h.day === todayIdx);
                const toMinutes = (t) => {
                  if (!t) return null;
                  const [hh, mm] = t.split(":").map(Number);
                  if (isNaN(hh)) return null;
                  return hh * 60 + (mm || 0);
                };
                const nowMin = now.getHours() * 60 + now.getMinutes();
                let hoursStatus = "Hours not available";
                let isOpenNow = false;
                if (hours.length > 0) {
                  if (!todayRow || todayRow.is_closed) {
                    // Find next open day
                    let nextOpen = null;
                    for (let i = 1; i <= 7; i += 1) {
                      const dIdx = (todayIdx + i) % 7;
                      const row = hours.find((h) => h.day === dIdx);
                      if (row && !row.is_closed && row.open_time) {
                        nextOpen = { dIdx, row };
                        break;
                      }
                    }
                    hoursStatus = nextOpen
                      ? `Closed — opens ${dayShort[nextOpen.dIdx]} ${fmt(nextOpen.row.open_time)}`
                      : "Closed today";
                  } else {
                    const openMin = toMinutes(todayRow.open_time);
                    const closeMin = toMinutes(todayRow.close_time);
                    if (openMin != null && nowMin < openMin) {
                      hoursStatus = `Opens at ${fmt(todayRow.open_time)}`;
                    } else if (closeMin != null && nowMin >= closeMin) {
                      // Closed for today
                      let nextOpen = null;
                      for (let i = 1; i <= 7; i += 1) {
                        const dIdx = (todayIdx + i) % 7;
                        const row = hours.find((h) => h.day === dIdx);
                        if (row && !row.is_closed && row.open_time) {
                          nextOpen = { dIdx, row };
                          break;
                        }
                      }
                      hoursStatus = nextOpen
                        ? `Closed — opens ${dayShort[nextOpen.dIdx]} ${fmt(nextOpen.row.open_time)}`
                        : "Closed";
                    } else if (closeMin != null) {
                      isOpenNow = true;
                      hoursStatus = `Open until ${fmt(todayRow.close_time)}`;
                    }
                  }
                }

                const Row = ({
                  label,
                  value,
                  testId,
                  trailing,
                  onValueClick,
                  isLast,
                }) => (
                  <div
                    className={`flex items-center justify-between gap-4 py-4 ${isLast ? "" : "border-b border-gray-100"}`}
                    data-testid={testId}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-base font-bold text-gray-900 mb-1">
                        {label}
                      </p>
                      {onValueClick ? (
                        <button
                          type="button"
                          onClick={onValueClick}
                          className="text-sm text-gray-500 hover:text-gray-700 break-words text-left"
                        >
                          {value}
                        </button>
                      ) : (
                        <p className="text-sm text-gray-500 break-words whitespace-pre-line">
                          {value}
                        </p>
                      )}
                    </div>
                    {trailing}
                  </div>
                );

                return (
                  <section className="bg-white rounded-2xl border border-gray-100 px-4">
                    {business.address && (
                      <Row
                        label="Address"
                        value={business.address}
                        testId="about-address"
                        trailing={
                          <a
                            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(business.address)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="Open in maps"
                            className="w-11 h-11 rounded-xl bg-gray-100 hover:bg-gray-200 flex items-center justify-center flex-shrink-0 transition-colors"
                            data-testid="about-address-action"
                          >
                            <Navigation className="w-5 h-5 text-gray-900" />
                          </a>
                        }
                      />
                    )}
                    {business.phone && (
                      <Row
                        label="Phone"
                        value={business.phone}
                        testId="about-phone"
                        trailing={
                          <a
                            href={`tel:${business.phone}`}
                            aria-label="Call"
                            className="w-11 h-11 rounded-xl bg-gray-100 hover:bg-gray-200 flex items-center justify-center flex-shrink-0 transition-colors"
                            data-testid="about-phone-action"
                          >
                            <Phone className="w-5 h-5 text-gray-900" />
                          </a>
                        }
                      />
                    )}
                    {business.email && (
                      <Row
                        label="Email"
                        value={business.email}
                        testId="about-email"
                        trailing={
                          <a
                            href={`mailto:${business.email}`}
                            aria-label="Email"
                            className="w-11 h-11 rounded-xl bg-gray-100 hover:bg-gray-200 flex items-center justify-center flex-shrink-0 transition-colors"
                            data-testid="about-email-action"
                          >
                            <Mail className="w-5 h-5 text-gray-900" />
                          </a>
                        }
                      />
                    )}
                    {hours.length > 0 && (
                      <div data-testid="about-hours">
                        <Row
                          label="Hours"
                          value={hoursStatus}
                          isLast
                          trailing={
                            <button
                              type="button"
                              onClick={() => setHoursExpanded((v) => !v)}
                              aria-label={
                                hoursExpanded
                                  ? "Hide weekly hours"
                                  : "Show weekly hours"
                              }
                              aria-expanded={hoursExpanded}
                              className="w-11 h-11 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center flex-shrink-0 transition-all"
                              data-testid="about-hours-toggle"
                            >
                              <ChevronDown
                                className={`w-5 h-5 text-gray-900 transition-transform duration-200 ${hoursExpanded ? "rotate-180" : ""}`}
                              />
                            </button>
                          }
                        />
                        <div
                          className={`grid transition-all duration-300 ease-out ${hoursExpanded ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}
                        >
                          <div className="overflow-hidden">
                            <div
                              className="pb-4 pt-1"
                              data-testid="about-hours-week"
                            >
                              {dayLabels.map((label, idx) => {
                                const row = hours.find((h) => h.day === idx);
                                const isToday = idx === todayIdx;
                                const closed = !row || row.is_closed;
                                return (
                                  <div
                                    key={idx}
                                    className="flex items-center justify-between py-2 text-sm"
                                    data-testid={`hours-row-${idx}`}
                                  >
                                    <span
                                      className={
                                        isToday
                                          ? "font-semibold"
                                          : "text-gray-700"
                                      }
                                      style={
                                        isToday
                                          ? { color: branding.primaryColor }
                                          : {}
                                      }
                                    >
                                      {label}
                                      {isToday && (
                                        <span
                                          className="ml-2 text-xs font-semibold px-1.5 py-0.5 rounded-full"
                                          style={{
                                            backgroundColor: `${branding.primaryColor}15`,
                                            color: branding.primaryColor,
                                          }}
                                        >
                                          Today
                                        </span>
                                      )}
                                    </span>
                                    <span
                                      className={
                                        closed
                                          ? "text-gray-400"
                                          : isToday
                                            ? "text-gray-900 font-medium"
                                            : "text-gray-700"
                                      }
                                    >
                                      {closed
                                        ? "Closed"
                                        : `${fmt(row.open_time)} – ${fmt(row.close_time)}`}
                                    </span>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </section>
                );
              })()}
            </div>
          )}

          {/* Team Tab */}
          {step === 0 && activeTab === "team" && (
            <div className="space-y-4" data-testid="tab-content-team">
              <h2 className="text-xl font-bold">Our team</h2>
              {(() => {
                const team = (businessData?.staff || []).filter(
                  (m) => m.is_active !== false,
                );
                if (team.length === 0) {
                  return (
                    <div
                      className="text-center py-10 bg-gray-50 rounded-2xl"
                      data-testid="team-empty"
                    >
                      <UserIcon className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                      <p className="text-sm text-gray-500">
                        Team details coming soon.
                      </p>
                    </div>
                  );
                }
                return (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {team.map((member) => {
                      const title =
                        (member.title || "").trim() || "Beauty Therapist";
                      const initial =
                        (member.name || "?").trim().charAt(0).toUpperCase() ||
                        "?";
                      return (
                        <div
                          key={member.id}
                          data-testid={`team-member-${member.id}`}
                          className="flex items-center gap-3 p-4 bg-white border-2 border-gray-100 rounded-2xl hover:border-gray-200 hover:shadow-sm transition-all"
                        >
                          {member.avatar_url ? (
                            <img
                              src={member.avatar_url}
                              alt={member.name}
                              className="w-14 h-14 rounded-full object-cover flex-shrink-0"
                            />
                          ) : (
                            <div
                              className="w-14 h-14 rounded-full flex items-center justify-center flex-shrink-0 text-white font-bold text-lg"
                              style={{
                                backgroundColor: branding.primaryColor,
                              }}
                            >
                              {initial}
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold text-gray-900 truncate">
                              {member.name || "Team member"}
                            </p>
                            <p className="text-sm text-gray-500 truncate">
                              {title}
                            </p>
                            {(member.bio || "").trim() && (
                              <button
                                type="button"
                                onClick={() =>
                                  setBioModal({ open: true, member })
                                }
                                className="text-xs font-medium mt-1 hover:underline"
                                style={{ color: branding.primaryColor }}
                                data-testid={`team-view-bio-${member.id}`}
                              >
                                View bio
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
          )}

          {/* Staff Selection */}
          {stepInfo.phase === "staff" && currentService && (
            <div className="space-y-5" data-testid="staff-selection-step">
              <div
                className="p-4 rounded-2xl"
                style={{
                  backgroundColor: `${branding.primaryColor}08`,
                  borderLeft: `4px solid ${branding.primaryColor}`,
                }}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">
                      Choose a staff member for:
                    </p>
                    <h3
                      className="text-lg font-bold"
                      style={{ color: branding.primaryColor }}
                    >
                      {currentService.name}
                    </h3>
                  </div>
                  <div className="text-right">
                    <span className="text-sm text-gray-500">
                      {currentService.duration} min
                    </span>
                    <p
                      className="font-semibold"
                      style={{ color: branding.primaryColor }}
                    >
                      ${currentService.price.toFixed(2)}
                    </p>
                  </div>
                </div>
                {selectedServices.length > 1 && (
                  <div className="mt-3 flex gap-1.5">
                    {selectedServices.map((id, idx) => (
                      <div
                        key={id}
                        className={`h-1.5 flex-1 rounded-full transition-all ${idx <= currentServiceIdx ? "" : "opacity-30"}`}
                        style={{ backgroundColor: branding.primaryColor }}
                      />
                    ))}
                  </div>
                )}
              </div>

              {(() => {
                const assigned = currentService.assigned_staff_ids || [];
                const allActiveStaff = (businessData?.staff || []).filter(
                  (m) => m.is_active !== false,
                );
                const eligible =
                  assigned.length > 0
                    ? allActiveStaff.filter((m) => assigned.includes(m.id))
                    : allActiveStaff;
                const selectedStaffId = currentBooking?.staff_id || null;
                const isAnyoneSelected =
                  currentBooking?.staff_chosen === true && !selectedStaffId;

                const StaffCard = ({
                  isAnyone,
                  member,
                  selected,
                  onSelect,
                }) => {
                  const initial = isAnyone
                    ? null
                    : (member.name || "?").trim().charAt(0).toUpperCase() ||
                      "?";
                  return (
                    <div
                      onClick={onSelect}
                      data-testid={
                        isAnyone
                          ? "staff-card-anyone"
                          : `staff-card-${member.id}`
                      }
                      className={`relative flex items-center gap-3 p-4 rounded-2xl border-2 transition-all cursor-pointer ${
                        selected
                          ? "shadow-md"
                          : "border-gray-200 hover:border-gray-300 hover:shadow-sm bg-white"
                      }`}
                      style={
                        selected
                          ? {
                              borderColor: branding.primaryColor,
                              backgroundColor: `${branding.primaryColor}05`,
                            }
                          : {}
                      }
                    >
                      {isAnyone ? (
                        <div
                          className="w-14 h-14 rounded-full flex items-center justify-center flex-shrink-0"
                          style={{
                            backgroundColor: `${branding.primaryColor}15`,
                          }}
                        >
                          <Users
                            className="w-6 h-6"
                            style={{ color: branding.primaryColor }}
                          />
                        </div>
                      ) : member.avatar_url ? (
                        <img
                          src={member.avatar_url}
                          alt={member.name}
                          className="w-14 h-14 rounded-full object-cover flex-shrink-0"
                        />
                      ) : (
                        <div
                          className="w-14 h-14 rounded-full flex items-center justify-center flex-shrink-0 text-white font-bold text-lg"
                          style={{ backgroundColor: branding.primaryColor }}
                        >
                          {initial}
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-semibold text-gray-900 truncate">
                            {isAnyone
                              ? "Choose Anyone"
                              : member.name || "Team member"}
                          </p>
                          {isAnyone && (
                            <span
                              className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full"
                              style={{
                                backgroundColor: `${branding.primaryColor}15`,
                                color: branding.primaryColor,
                              }}
                            >
                              Recommended
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-500 truncate">
                          {isAnyone
                            ? "Book with the first available team member."
                            : (member.title || "").trim() ||
                              "Beauty Therapist"}
                        </p>
                        {!isAnyone && (member.bio || "").trim() && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setBioModal({ open: true, member });
                            }}
                            className="text-xs font-medium mt-1 hover:underline"
                            style={{ color: branding.primaryColor }}
                            data-testid={`staff-view-bio-${member.id}`}
                          >
                            View bio
                          </button>
                        )}
                      </div>
                      {selected && (
                        <div
                          className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0"
                          style={{ backgroundColor: branding.primaryColor }}
                        >
                          <CheckCircle className="w-4 h-4 text-white" />
                        </div>
                      )}
                    </div>
                  );
                };

                return (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <StaffCard
                      isAnyone
                      selected={isAnyoneSelected}
                      onSelect={() =>
                        updateCurrentBooking({
                          staff_id: null,
                          staff_chosen: true,
                          // changing staff invalidates previously picked time
                          time: "",
                        })
                      }
                    />
                    {eligible.length === 0 ? (
                      <div
                        className="p-4 bg-gray-50 rounded-2xl text-center text-sm text-gray-500"
                        data-testid="staff-eligible-empty"
                      >
                        No specific staff assigned to this service yet — pick
                        "Choose Anyone" above.
                      </div>
                    ) : (
                      eligible.map((m) => (
                        <StaffCard
                          key={m.id}
                          member={m}
                          selected={selectedStaffId === m.id}
                          onSelect={() =>
                            updateCurrentBooking({
                              staff_id: m.id,
                              staff_chosen: true,
                              time: "",
                            })
                          }
                        />
                      ))
                    )}
                  </div>
                );
              })()}
            </div>
          )}

          {/* Date & Time Selection */}
          {stepInfo.phase === "datetime" && currentService && (
            <div className="space-y-5">
              <div
                className="p-4 rounded-2xl"
                style={{
                  backgroundColor: `${branding.primaryColor}08`,
                  borderLeft: `4px solid ${branding.primaryColor}`,
                }}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Selecting time for:</p>
                    <h3
                      className="text-lg font-bold"
                      style={{ color: branding.primaryColor }}
                    >
                      {currentService.name}
                    </h3>
                  </div>
                  <div className="text-right">
                    <span className="text-sm text-gray-500">
                      {currentService.duration} min
                    </span>
                    <p
                      className="font-semibold"
                      style={{ color: branding.primaryColor }}
                    >
                      ${currentService.price.toFixed(2)}
                    </p>
                  </div>
                </div>
                {selectedServices.length > 1 && (
                  <div className="mt-3 flex gap-1.5">
                    {selectedServices.map((id, idx) => (
                      <div
                        key={id}
                        className={`h-1.5 flex-1 rounded-full transition-all ${idx <= currentServiceIdx ? "" : "opacity-30"}`}
                        style={{ backgroundColor: branding.primaryColor }}
                      />
                    ))}
                  </div>
                )}
              </div>

              {currentServiceIdx > 0 && (
                <div className="p-3 bg-green-50 rounded-xl border border-green-100">
                  <p className="text-sm font-medium text-green-800 mb-2">
                    ✓ Already selected:
                  </p>
                  {serviceBookings.slice(0, currentServiceIdx).map((b, idx) => {
                    const svc = businessData?.services?.find(
                      (s) => s.id === b.service_id,
                    );
                    return (
                      <div
                        key={idx}
                        className="text-sm text-green-700 flex justify-between"
                      >
                        <span>{svc?.name}</span>
                        <span>
                          {b.date?.toLocaleDateString("en-US", {
                            month: "short",
                            day: "numeric",
                          })}{" "}
                          at {b.time}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}

              <div className="bg-gray-50 rounded-2xl p-4">
                <CustomCalendar
                  selected={currentBooking?.date}
                  onSelect={(date) => {
                    updateCurrentBooking({ date, time: "" });
                    fetchAvailability(
                      selectedServices[currentServiceIdx],
                      date,
                      getExcludeSlots(),
                      currentBooking?.staff_id || null,
                    );
                  }}
                  businessHours={business?.business_hours || []}
                />
              </div>

              {currentBooking?.date && (
                <div>
                  <h3 className="font-semibold mb-3">Available Times</h3>
                  {availability.length === 0 ? (
                    <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-2xl">
                      <Clock className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                      No available times for this date.
                    </div>
                  ) : (
                    <div className="grid grid-cols-3 md:grid-cols-4 gap-2">
                      {availability.map((slot) => {
                        const isSelected = currentBooking?.time === slot.time;
                        const isPast = isTimeSlotPast(
                          slot.time,
                          currentBooking?.date,
                        );
                        return (
                          <button
                            key={slot.time}
                            onClick={() =>
                              !isPast &&
                              updateCurrentBooking({ time: slot.time })
                            }
                            disabled={isPast}
                            className={`py-3 px-3 rounded-xl border-2 transition-all text-sm font-semibold 
                              ${
                                isPast
                                  ? "border-gray-100 bg-gray-50 text-gray-300 cursor-not-allowed"
                                  : isSelected
                                    ? "text-white border-transparent shadow-md"
                                    : "border-gray-200 hover:border-gray-300 bg-white"
                              }`}
                            style={
                              isSelected && !isPast
                                ? { backgroundColor: branding.primaryColor }
                                : {}
                            }
                          >
                            {slot.time}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Customer Details */}
          {stepInfo.phase === "details" && (
            <div className="space-y-4">
              <h2 className="text-xl font-bold">Your Details</h2>
              <div className="grid gap-4">
                <div>
                  <Label htmlFor="name" className="text-sm font-medium">
                    Name *
                  </Label>
                  <Input
                    id="name"
                    placeholder="Your full name"
                    value={customerInfo.customer_name}
                    onChange={(e) =>
                      setCustomerInfo((prev) => ({
                        ...prev,
                        customer_name: e.target.value,
                      }))
                    }
                    className="mt-1.5 h-12 rounded-xl"
                  />
                </div>
                <div>
                  <Label htmlFor="email" className="text-sm font-medium">
                    Email *
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="your@email.com"
                    value={customerInfo.customer_email}
                    onChange={(e) =>
                      setCustomerInfo((prev) => ({
                        ...prev,
                        customer_email: e.target.value,
                      }))
                    }
                    className="mt-1.5 h-12 rounded-xl"
                  />
                </div>
                <div>
                  <Label htmlFor="phone" className="text-sm font-medium">
                    Phone (Optional)
                  </Label>
                  <Input
                    id="phone"
                    type="tel"
                    placeholder="Your phone number"
                    value={customerInfo.customer_phone}
                    onChange={(e) =>
                      setCustomerInfo((prev) => ({
                        ...prev,
                        customer_phone: e.target.value,
                      }))
                    }
                    className="mt-1.5 h-12 rounded-xl"
                  />
                </div>
                <div>
                  <Label htmlFor="notes" className="text-sm font-medium">
                    Notes (Optional)
                  </Label>
                  <Textarea
                    id="notes"
                    placeholder="Any special requests"
                    value={customerInfo.notes}
                    onChange={(e) =>
                      setCustomerInfo((prev) => ({
                        ...prev,
                        notes: e.target.value,
                      }))
                    }
                    className="mt-1.5 rounded-xl"
                    rows={3}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Confirm */}
          {stepInfo.phase === "confirm" && (
            <div className="space-y-4">
              <h2 className="text-xl font-bold">Confirm Booking</h2>

              <div className="bg-gray-50 rounded-2xl p-4 space-y-4">
                {serviceBookings.map((booking, idx) => {
                  const service = businessData?.services?.find(
                    (s) => s.id === booking.service_id,
                  );
                  return (
                    <div
                      key={idx}
                      className={idx > 0 ? "pt-4 border-t border-gray-200" : ""}
                    >
                      {serviceBookings.length > 1 && (
                        <div className="text-xs font-medium text-gray-500 mb-2">
                          Appointment {idx + 1}
                        </div>
                      )}
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Service</span>
                          <span className="font-medium">{service?.name}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Date</span>
                          <span className="font-medium">
                            {booking.date?.toLocaleDateString("en-US", {
                              weekday: "short",
                              month: "short",
                              day: "numeric",
                            })}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Time</span>
                          <span className="font-medium">{booking.time}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Duration</span>
                          <span className="font-medium">
                            {service?.duration} min
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Price</span>
                          <span
                            className="font-semibold"
                            style={{ color: branding.primaryColor }}
                          >
                            ${service?.price?.toFixed(2)}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}

                {serviceBookings.length > 1 && (
                  <div className="pt-4 border-t-2 border-gray-300">
                    <div className="flex justify-between">
                      <span className="font-semibold">Total</span>
                      <span
                        className="font-bold text-xl"
                        style={{ color: branding.primaryColor }}
                      >
                        ${getTotalPrice().toFixed(2)}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* Payment breakdown */}
              {paymentSettings?.deposit_required &&
                paymentSettings?.deposit_percentage > 0 && (
                  <div
                    className="bg-blue-50 rounded-2xl p-4"
                    data-testid="payment-breakdown"
                  >
                    <div className="flex items-center gap-2 mb-3">
                      <CreditCard className="w-5 h-5 text-blue-600" />
                      <h3 className="font-semibold text-blue-900">Payment</h3>
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-blue-700">
                          Deposit ({paymentSettings.deposit_percentage}%)
                        </span>
                        <span className="font-semibold text-blue-900">
                          ${getDepositAmount().toFixed(2)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-blue-700">
                          Due at appointment
                        </span>
                        <span className="font-medium text-blue-800">
                          ${getRemainingAmount().toFixed(2)}
                        </span>
                      </div>
                      <div className="pt-2 mt-2 border-t border-blue-200">
                        <p className="text-xs text-blue-600">
                          You'll be redirected to Stripe to complete the deposit
                          payment securely.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

              <div className="bg-gray-50 rounded-2xl p-4">
                <h3 className="font-semibold mb-2">Your Information</h3>
                <div className="space-y-1 text-sm">
                  <p>
                    <span className="text-gray-500">Name:</span>{" "}
                    {customerInfo.customer_name}
                  </p>
                  <p>
                    <span className="text-gray-500">Email:</span>{" "}
                    {customerInfo.customer_email}
                  </p>
                  {customerInfo.customer_phone && (
                    <p>
                      <span className="text-gray-500">Phone:</span>{" "}
                      {customerInfo.customer_phone}
                    </p>
                  )}
                  {customerInfo.notes && (
                    <p>
                      <span className="text-gray-500">Notes:</span>{" "}
                      {customerInfo.notes}
                    </p>
                  )}
                </div>
              </div>

              <p className="text-xs text-gray-500">
                By confirming this booking, you agree to receive email
                notifications about your appointment.
              </p>
            </div>
          )}
        </div>
      </main>

      {/* Fixed Bottom Section - Buttons + Footer */}
      <div className="flex-shrink-0 bg-white border-t border-gray-200">
        <div className="px-4 py-3">
          <div className="flex justify-between gap-3 max-w-4xl mx-auto">
            <Button
              variant="outline"
              onClick={handleBack}
              disabled={step === 0}
              className={`h-12 px-6 rounded-xl transition-all flex-1 max-w-[140px] ${step === 0 ? "opacity-0 pointer-events-none" : ""}`}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>

            {stepInfo.phase === "confirm" ? (
              <Button
                onClick={handleSubmit}
                disabled={submitting}
                className="h-12 px-6 rounded-xl text-white transition-all flex-1"
                style={{ backgroundColor: branding.primaryColor }}
                data-testid="confirm-booking-btn"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {paymentSettings?.deposit_required
                      ? "Processing..."
                      : "Booking..."}
                  </>
                ) : paymentSettings?.deposit_required &&
                  paymentSettings?.deposit_percentage > 0 ? (
                  <>
                    <CreditCard className="w-4 h-4 mr-2" />
                    Pay Deposit ${getDepositAmount().toFixed(2)}
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Confirm Booking
                  </>
                )}
              </Button>
            ) : step === 0 && activeTab !== "services" ? (
              <Button
                onClick={() => setActiveTab("services")}
                className="h-12 px-6 rounded-xl text-white transition-all flex-1"
                style={{ backgroundColor: branding.primaryColor }}
                data-testid="goto-services-btn"
              >
                Book now
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button
                onClick={handleNext}
                disabled={!canProceed()}
                className="h-12 px-6 rounded-xl text-white transition-all flex-1"
                style={{
                  backgroundColor: canProceed()
                    ? branding.primaryColor
                    : "#d1d5db",
                }}
                data-testid="next-step-btn"
              >
                {stepInfo.phase === "datetime" &&
                currentServiceIdx < selectedServices.length - 1
                  ? `Next: ${businessData?.services?.find((s) => s.id === selectedServices[currentServiceIdx + 1])?.name?.substring(0, 15) || "Next"}...`
                  : "Next"}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            )}
          </div>
        </div>

        <footer className="border-t border-gray-100 py-3 bg-gray-50 safe-area-bottom">
          <div className="text-center text-xs text-gray-500">
            © {new Date().getFullYear()} {business.name}. Powered by SlotBook.
          </div>
        </footer>
      </div>

      {/* Full-description modal (opened via ⓘ icon on a clamped description) */}
      <Dialog
        open={descriptionModal.open}
        onOpenChange={(open) =>
          setDescriptionModal((prev) => ({ ...prev, open }))
        }
      >
        <DialogContent
          className="max-w-md sm:max-w-lg"
          data-testid="service-description-dialog"
        >
          <DialogHeader>
            <DialogTitle className="text-lg font-bold text-gray-900 pr-6">
              {descriptionModal.name}
            </DialogTitle>
            <DialogDescription className="sr-only">
              Full service description
            </DialogDescription>
          </DialogHeader>
          <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-line">
            {descriptionModal.description}
          </p>
        </DialogContent>
      </Dialog>

      {/* Staff Bio modal */}
      <Dialog
        open={bioModal.open}
        onOpenChange={(open) =>
          setBioModal((prev) => ({ ...prev, open, member: open ? prev.member : null }))
        }
      >
        <DialogContent
          className="max-w-md sm:max-w-lg"
          data-testid="staff-bio-dialog"
        >
          <DialogHeader>
            <div className="flex items-center gap-4 pr-6">
              {bioModal.member?.avatar_url ? (
                <img
                  src={bioModal.member.avatar_url}
                  alt={bioModal.member.name}
                  className="w-16 h-16 rounded-full object-cover flex-shrink-0"
                />
              ) : (
                <div
                  className="w-16 h-16 rounded-full flex items-center justify-center flex-shrink-0 text-white font-bold text-xl"
                  style={{ backgroundColor: branding.primaryColor }}
                >
                  {(bioModal.member?.name || "?")
                    .trim()
                    .charAt(0)
                    .toUpperCase() || "?"}
                </div>
              )}
              <div className="flex-1 min-w-0 text-left">
                <DialogTitle className="text-lg font-bold text-gray-900">
                  {bioModal.member?.name || "Team member"}
                </DialogTitle>
                <p className="text-sm text-gray-500 mt-0.5">
                  {(bioModal.member?.title || "").trim() || "Beauty Therapist"}
                </p>
              </div>
            </div>
            <DialogDescription className="sr-only">
              Staff biography
            </DialogDescription>
          </DialogHeader>
          {(bioModal.member?.bio || "").trim() ? (
            <p
              className="text-sm text-gray-700 leading-relaxed whitespace-pre-line"
              data-testid="staff-bio-content"
            >
              {bioModal.member.bio}
            </p>
          ) : (
            <p className="text-sm text-gray-400 italic">No bio available.</p>
          )}
        </DialogContent>
      </Dialog>

      {/* Max services exceeded — nudge customer to contact the salon */}
      <Dialog
        open={maxServicesModalOpen}
        onOpenChange={setMaxServicesModalOpen}
      >
        <DialogContent
          className="max-w-md"
          data-testid="max-services-dialog"
        >
          <DialogHeader>
            <DialogTitle className="text-lg font-bold text-gray-900 text-left">
              Booking more than {MAX_SERVICES} services
            </DialogTitle>
            <DialogDescription className="sr-only">
              Contact the salon to book more than {MAX_SERVICES} services.
            </DialogDescription>
          </DialogHeader>
          <p className="text-sm text-gray-700 leading-relaxed">
            To book more than {MAX_SERVICES} services, please contact us so we
            can confirm we're able to cater for additional services and have
            enough availability.
          </p>
          {business?.phone && (
            <a
              href={`tel:${business.phone}`}
              data-testid="max-services-call-link"
              className="flex items-center gap-3 mt-2 p-3 rounded-2xl border-2 transition-all hover:shadow-sm"
              style={{
                borderColor: branding.primaryColor,
                backgroundColor: `${branding.primaryColor}08`,
              }}
            >
              <div
                className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: `${branding.primaryColor}20` }}
              >
                <Phone
                  className="w-5 h-5"
                  style={{ color: branding.primaryColor }}
                />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-gray-500 mb-0.5">
                  Call {business.name || "the salon"}
                </p>
                <p
                  className="text-base font-bold"
                  style={{ color: branding.primaryColor }}
                >
                  {business.phone}
                </p>
              </div>
            </a>
          )}
          <Button
            type="button"
            onClick={() => setMaxServicesModalOpen(false)}
            className="w-full h-11 rounded-xl text-white mt-2"
            style={{ backgroundColor: branding.primaryColor }}
            data-testid="max-services-close-btn"
          >
            Got it
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PublicBookingPage;
